import * as React from "react";
import { useQuery } from "react-query";
import {
  AddressMessage,
  useOkHiMessageSender,
} from "../hooks/useOkHiMessageSender";
import {
  useAddressGeopoint,
  useAddressStreetviewData,
  useAddressToTheDoorData,
} from "./AddressStateProvider";

type AcquireAddressState = "incomplete" | "waitForRoadNames" | "complete";

const AcquireAddressStateContext = React.createContext<{
  acquireAddressState: AcquireAddressState;
  setAcquireAddressState: React.Dispatch<
    React.SetStateAction<AcquireAddressState>
  >;
} | null>(null);

interface Props {
  children: React.ReactNode;
}

export function AcquireAddressStateProvider({ children }: Props) {
  const { status, data: placeSuggestions } = usePlaceSuggestions();
  const { sendMessage } = useOkHiMessageSender();
  const { addressGeoPoint } = useAddressGeopoint();
  const { addressStreetviewData } = useAddressStreetviewData();
  const { addressToTheDoorData } = useAddressToTheDoorData();
  const [addressState, setAddressState] =
    React.useState<AcquireAddressState>("incomplete");

  const acquireAddressState = React.useMemo(
    () => ({
      acquireAddressState: addressState,
      setAcquireAddressState: setAddressState,
    }),
    [addressState]
  );

  React.useEffect(() => {
    if (status !== "loading" && addressState === "waitForRoadNames") {
      setAddressState("complete");
    }
  }, [status, addressState]);

  React.useEffect(() => {
    if (
      addressState === "complete" &&
      (status !== "loading" || addressToTheDoorData)
    ) {
      const message: AddressMessage = {
        type: "addressMessage",
        geoPoint: addressGeoPoint,
        streetviewData: addressStreetviewData,
        toTheDoorData: addressToTheDoorData,
        roadNames: placeSuggestions?.roadNames || [],
      };
      sendMessage(message);
    }
  }, [
    addressState,
    addressGeoPoint,
    addressStreetviewData,
    addressToTheDoorData,
    placeSuggestions,
    sendMessage,
    status,
  ]);

  return (
    <AcquireAddressStateContext.Provider value={acquireAddressState}>
      {children}
    </AcquireAddressStateContext.Provider>
  );
}

export function useAcquireAddressState() {
  const context = React.useContext(AcquireAddressStateContext);
  if (context == null) {
    throw new Error(
      "useAcquireAddressState must be used within AcquireAddressStateContext"
    );
  }
  return context;
}

export function usePlaceSuggestions() {
  const { addressGeoPoint: geoPoint } = useAddressGeopoint();
  return useQuery(
    ["placeSuggestions", geoPoint?.lat, geoPoint?.lng],
    async () => ({ roadNames: [], buildingNames: [] }),
    {
      enabled: geoPoint != null,
      refetchOnWindowFocus: false,
    }
  );
}
